import { container } from 'src/StaticContainer';
import type { RootState } from 'src/store';

/** Calendar date in `YYYY-MM-DD` form. */
export type FeatureFlagDate = `${number}-${number}-${number}`;

export interface FeatureFlagOptions<T> {
    /**
     * The key created in the LaunchDarkly interface to identify the feature flag.
     */
    key: string;
    /**
     * The date the flag was introduced, used to sort flags by recency in the feature flag devtools.
     */
    createdOn: FeatureFlagDate;
    /**
     * Optional summary of what the flag controls, shown in the feature flag devtools.
     */
    description?: string;
    /**
     * The default value to use if the feature flag is not found or the user is not in the targeting.
     */
    defaultValue: T;
    /**
     * Optional function to parse the raw feature flag value into the expected type.
     */
    parseValue?: (value: unknown) => T;
}

export class FeatureFlag<T> {
    constructor(options: FeatureFlagOptions<T>) {
        this.#key = options.key;
        this.#createdOn = options.createdOn;
        this.#description = options.description;
        this.#defaultValue = options.defaultValue;
        this.#parseValue = options.parseValue;
    }

    #defaultValue: T;

    get defaultValue(): T {
        return this.#defaultValue;
    }

    #key: string;

    get key(): string {
        return this.#key;
    }

    #createdOn: FeatureFlagDate;

    get createdOn(): FeatureFlagDate {
        return this.#createdOn;
    }

    #description?: string;

    get description(): string | undefined {
        return this.#description;
    }

    #parseValue?: (value: unknown) => T;

    parseValue(value: unknown): T {
        if (this.#parseValue) {
            try {
                return this.#parseValue(value);
            } catch (err) {
                container
                    .get('Logger')
                    .getSubLogger({ name: 'FeatureFlag' })
                    .warn({
                        message: `Failed to parse feature flag value for key "${this.#key}". Using default value.`,
                        value,
                        err,
                    });
                return this.#defaultValue;
            }
        }
        return value as T;
    }

    /**
     * @deprecated Use FeatureFlagService.getSignal() or FeatureFlagService.getValue() instead
     */
    select = (state: RootState): T => {
        return (state.featureFlags.flags[this.#key] as T) ?? this.#defaultValue;
    };
}
